import { TableActionGroup, TableActionButton } from '@channelplay/table-actions';
// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal/form layout from the design system)
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import FormModal from '../components/FormModal';
import { ActionsSkeletonCell } from '../components/ActionsSkeletonCell';
import { AccessDeniedPanel } from '../components/AccessDeniedPanel';
import { FormErrorBanner } from '../components/FormErrorBanner';
import { NoProjectSelectedState } from '../components/NoProjectSelectedState';
import { Skeleton } from '../components/Skeleton';
import { InlineLoader } from '../components/loading';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, X, MapPin, Store as StoreIcon, AlertTriangle, CheckCircle2 } from 'lucide-react';
import EmployeeTags from '../components/EmployeeTags';
import { onboardingAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';

/**
 * Grow → Onboarding Setup → Buddy Pool.
 *
 * The curated list of experienced employees who can host a new joiner for their
 * shadow training week, tagged with the area they cover. Dashboard → Onboarding
 * ranks these first when suggesting a buddy; anyone not on the list still
 * appears there, but only as a lower-ranked "nearby colleague" fallback.
 *
 * Adding is AREA-FIRST — pick a state, then a city, then whoever works there —
 * because that matches how the list is actually built: you are staffing a
 * region, not hunting for one known person.
 *
 * ⚠️ "Area" is the employee's STORE location, not their personal record. The
 * 1Office roster carries a city only sometimes and a state almost never
 * (GET /employees/master has no state column at all), so filtering on the
 * employee's own details would return almost nobody. Stores carry both.
 *
 * A pool member with no current store is shown with a warning rather than
 * hidden: the placement puts the trainee at the buddy's store, so a buddy with
 * none is unusable until they are deployed again — worth surfacing to whoever
 * maintains the list, not silently filtering away.
 */
interface BuddyPoolEntry {
  id: number;
  employeeCode: string;
  employeeName: string | null;
  designation: string | null;
  state: string | null;
  city: string | null;
  active: boolean;
  note: string | null;
  currentStoreName: string | null;
  onNotice: boolean;
  noticeLastWorkingDay: string | null;
  storeCode: string | null;
  storeAddress: string | null;
  storeCity: string | null;
  storeState: string | null;
  storePinCode: string | null;
  storeTrade: string | null;
  storeCategory: string | null;
}

/** Same fold the server matches on, so drift is judged the same way. */
const locationKey = (v: string | null | undefined): string =>
  (v || '').trim().toUpperCase().replace(/&/g, 'AND').replace(/[^A-Z0-9]/g, '');

interface PoolCandidate {
  employeeCode: string;
  employeeName: string;
  /** 1Office budget name — "iPro" / "Senior iPro", same label the app shows. */
  designation: string;
  storeName: string;
  city: string;
  state: string;
  dateOfJoining: string | null;
  tenureLabel: string | null;
  inPool: boolean;
  onNotice: boolean;
  noticeLastWorkingDay: string | null;
}

/** A state that has at least one deployable buddy, with its cities. */
interface BuddyLocation {
  stateKey: string;
  state: string;
  employeeCount: number;
  cities: { cityKey: string; city: string; employeeCount: number }[];
}

const httpStatus = (err: unknown): number | null =>
  (err as { response?: { status?: number } })?.response?.status ?? null;

const apiMessage = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

export default function BuddyPoolPage({
  embedded, actionSlot,
}: { embedded?: boolean; actionSlot?: HTMLElement | null } = {}) {
  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const navigate = useNavigate();

  const [entries, setEntries] = useState<BuddyPoolEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<BuddyPoolEntry | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Non-fatal channel for row mutations, distinct from `error` which the table
  // treats as a FATAL full-table state.
  const [actionError, setActionError] = useState<string | null>(null);

  // --- Add flow state (area → employee) ---
  const [locations, setLocations] = useState<BuddyLocation[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [candidates, setCandidates] = useState<PoolCandidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [stateKey, setStateKey] = useState('');
  const [cityKey, setCityKey] = useState('');
  const [candidateQuery, setCandidateQuery] = useState('');
  const [picked, setPicked] = useState<PoolCandidate | null>(null);

  const resetForm = () => {
    setStateKey('');
    setCityKey('');
    setCandidateQuery('');
    setCandidates([]);
    setPicked(null);
  };

  const fetchPool = useCallback(async () => {
    if (!selectedProjectId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await onboardingAPI.getBuddyPool(selectedProjectId);
      setEntries(res.data?.data ?? []);
      setForbidden(null);
    } catch (err) {
      if (httpStatus(err) === 403) {
        setForbidden(apiMessage(err, 'You do not have access to the Buddy Pool for this project.'));
        return;
      }
      setForbidden(null);
      setError(apiMessage(err, 'Failed to load the buddy pool.'));
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    void fetchPool();
  }, [fetchPool]);

  const openAddModal = async () => {
    resetForm();
    setFormError(null);
    setIsModalOpen(true);
    if (!selectedProjectId || locations.length > 0) return;
    setLocationsLoading(true);
    try {
      const res = await onboardingAPI.getBuddyLocations(selectedProjectId);
      setLocations(res.data?.data ?? []);
    } catch (err) {
      setFormError(apiMessage(err, 'Could not load the list of areas.'));
    } finally {
      setLocationsLoading(false);
    }
  };

  // Candidates are fetched per area rather than all at once — an unfiltered call
  // returns every deployed employee on the project, which here is thousands.
  useEffect(() => {
    if (!isModalOpen || !selectedProjectId || !stateKey) {
      setCandidates([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setCandidatesLoading(true);
      try {
        const res = await onboardingAPI.getPoolCandidates({
          projectId: selectedProjectId,
          stateKey,
          cityKey: cityKey || undefined,
        });
        if (!cancelled) setCandidates(res.data?.data ?? []);
      } catch (err) {
        if (!cancelled) setFormError(apiMessage(err, 'Could not load employees for that area.'));
      } finally {
        if (!cancelled) setCandidatesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isModalOpen, selectedProjectId, stateKey, cityKey]);

  const selectedState = useMemo(
    () => locations.find((l) => l.stateKey === stateKey) ?? null,
    [locations, stateKey],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProjectId) return;
    if (!picked) {
      setFormError('Pick the employee to add as a buddy.');
      return;
    }
    setIsSubmitting(true);
    setFormError(null);
    try {
      await onboardingAPI.saveBuddyPoolEntry({
        projectId: selectedProjectId,
        employeeCode: picked.employeeCode,
        employeeName: picked.employeeName || null,
        designation: picked.designation || null,
        // Store the DISPLAY spellings of the area actually chosen, falling back
        // to the candidate's own store location when only a state was picked.
        state: selectedState?.state ?? picked.state ?? null,
        city: picked.city || null,
      });
      setIsModalOpen(false);
      resetForm();
      await fetchPool();
    } catch (err) {
      setFormError(apiMessage(err, 'Failed to add the buddy.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (entry: BuddyPoolEntry) => {
    if (!selectedProjectId) return;
    setIsSubmitting(true);
    setActionError(null);
    try {
      await onboardingAPI.deleteBuddyPoolEntry({ projectId: selectedProjectId, id: entry.id });
      setDeleteConfirm(null);
      await fetchPool();
    } catch (err) {
      setActionError(apiMessage(err, 'Failed to remove the buddy pool entry.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredEntries = useMemo(() => {
    if (!searchQuery) return entries;
    const q = searchQuery.toLowerCase();
    // Covers every column the table now shows — searching for a store, trade or
    // pincode that is visible on screen must find it.
    return entries.filter((e) =>
      [
        e.employeeName, e.employeeCode, e.designation,
        e.currentStoreName, e.storeCode, e.storeTrade, e.storeCategory,
        e.storeAddress, e.storeCity, e.storeState, e.storePinCode,
        e.city, e.state,
      ].some((v) => (v || '').toLowerCase().includes(q)),
    );
  }, [entries, searchQuery]);

  const filteredCandidates = useMemo(() => {
    const q = candidateQuery.trim().toLowerCase();
    const base = candidates.filter((c) => !c.inPool);
    if (!q) return base;
    return base.filter(
      (c) =>
        c.employeeName.toLowerCase().includes(q) ||
        c.employeeCode.toLowerCase().includes(q) ||
        c.storeName.toLowerCase().includes(q),
    );
  }, [candidates, candidateQuery]);

  const addButton = (
    <button
      className="flex-shrink-0 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
      onClick={openAddModal}
    >
      Add Buddy
    </button>
  );

  if (!selectedProjectId) {
    return (
      <div className="space-y-6">
        {!embedded && (
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Buddy Pool</h1>
            <p className="text-sm text-gray-500 mt-1">
              The state/city list of experienced employees who can host a new joiner for shadow training.
            </p>
          </div>
        )}
        <NoProjectSelectedState message="Select a project from the dropdown in the menu bar." />
      </div>
    );
  }

  if (forbidden) {
    return (
      <AccessDeniedPanel
        message={forbidden}
        onLogout={() => {
          clearAuth();
          navigate('/login');
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Buddy Pool</h1>
          <p className="text-sm text-gray-500 mt-1">
            Experienced employees who can host a new joiner for their shadow training week, tagged with the
            area they cover.
          </p>
        </div>
      )}

      {/* Embedded, the action lives in the shell's tab row so the two share one
          line. Standalone, it keeps its own right-aligned row. */}
      {actionSlot ? createPortal(addButton, actionSlot) : (
        <div className="flex items-center justify-end gap-4">{addButton}</div>
      )}

      {actionError && (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{actionError}</span>
          <button
            type="button"
            onClick={() => setActionError(null)}
            className="shrink-0 rounded p-0.5 text-red-500 hover:bg-red-100 hover:text-red-700"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
        <div className="border-b border-gray-100 p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, code, store, trade or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
              {/* One field per column. The table scrolls horizontally rather
                  than stacking values, so each is independently readable and
                  copyable. Actions stays pinned right through the scroll. */}
              <tr>
                <th className="whitespace-nowrap px-4 py-4">Employee</th>
                <th className="whitespace-nowrap px-4 py-4">Designation</th>
                <th className="whitespace-nowrap px-4 py-4">Store</th>
                <th className="whitespace-nowrap px-4 py-4">Trade</th>
                <th className="whitespace-nowrap px-4 py-4">Category</th>
                <th className="whitespace-nowrap px-4 py-4">Address</th>
                <th className="whitespace-nowrap px-4 py-4">City</th>
                <th className="whitespace-nowrap px-4 py-4">State</th>
                <th className="whitespace-nowrap px-4 py-4">Pincode</th>
                <th className="sticky right-0 z-10 whitespace-nowrap bg-gray-100 px-4 py-4 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white text-left">
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={index}>
                    {/* One placeholder per data column, widths roughly matching
                        the real content so the table doesn't jump on load. */}
                    {[140, 80, 64, 150, 80, 80, 48, 180, 96, 96, 64].map((w, i) => (
                      <td key={i} className="whitespace-nowrap px-4 py-4">
                        <Skeleton className="h-4" width={w} />
                      </td>
                    ))}
                    <ActionsSkeletonCell />
                  </tr>
                ))
              ) : error ? (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center">
                    <p className="text-sm text-red-500">{error}</p>
                    <button onClick={fetchPool} className="mt-2 text-sm text-blue-600 hover:underline">
                      Try again
                    </button>
                  </td>
                </tr>
              ) : filteredEntries.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center">
                    <p className="text-sm text-gray-500">
                      {entries.length === 0
                        ? 'No buddies yet. Add the experienced employees who can host new joiners, and Onboarding will suggest them by area.'
                        : 'No buddies match your search.'}
                    </p>
                    {searchQuery && (
                      <button onClick={() => setSearchQuery('')} className="mt-2 text-sm text-blue-600 hover:underline">
                        Clear search
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                filteredEntries.map((entry) => (
                  <tr key={entry.id} className="group hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-4 text-left">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{entry.employeeName || entry.employeeCode}</span>
                        {/* Designation has its own column; only the notice chip
                            belongs beside the name, as an exception marker. */}
                        <EmployeeTags
                          hideDesignation
                          onNotice={entry.onNotice}
                          lastWorkingDay={entry.noticeLastWorkingDay}
                        />
                      </div>
                      {/* Code sits UNDER the name, not beside it: it is an
                          identifier you check, not something you read every row. */}
                      <div className="mt-0.5 text-xs text-gray-500">{entry.employeeCode}</div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-left">
                      {entry.designation ? (
                        <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-800">
                          {entry.designation}
                        </span>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-4 text-left">
                      {entry.currentStoreName ? (
                        <>
                          <div className="flex items-center gap-1.5 whitespace-nowrap text-gray-900">
                            <StoreIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                            {entry.currentStoreName}
                          </div>
                          {/* The pool row keeps the area captured when the buddy
                              was added, but suggestions match on the LIVE store
                              location. They diverge after a redeploy, which is
                              exactly when someone needs to know. */}
                          {(locationKey(entry.city) !== locationKey(entry.storeCity)
                            || locationKey(entry.state) !== locationKey(entry.storeState)) && (
                            <div className="mt-1 flex items-start gap-1 text-[11px] text-amber-700">
                              <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
                              <span>
                                Moved — added under {[entry.city, entry.state].filter(Boolean).join(', ') || 'no area'}
                              </span>
                            </div>
                          )}
                          {entry.storeCode && (
                            <div className="mt-0.5 text-xs text-gray-500">{entry.storeCode}</div>
                          )}
                        </>
                      ) : (
                        // Not hidden: a buddy with no store cannot host anyone, and
                        // whoever maintains this list is the person who can fix it.
                        <div className="flex items-center gap-1.5 whitespace-nowrap text-amber-700">
                          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                          <span className="text-xs">Not deployed — cannot host</span>
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-left text-gray-900">{entry.storeTrade || '—'}</td>
                    <td className="whitespace-nowrap px-4 py-4 text-left">
                      {entry.storeCategory ? (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                          {entry.storeCategory}
                        </span>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-4 text-left">
                      {/* The one field that can be long — capped and wrapped so it
                          cannot stretch the row, with the full text on hover. */}
                      <div className="max-w-[16rem] text-xs text-gray-600" title={entry.storeAddress ?? undefined}>
                        {entry.storeAddress || '—'}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-left">
                      <span className="inline-flex items-center gap-1.5 text-gray-900">
                        <MapPin className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                        {entry.storeCity || '—'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-left text-gray-900">{entry.storeState || '—'}</td>
                    <td className="whitespace-nowrap px-4 py-4 text-left text-gray-600">{entry.storePinCode || '—'}</td>
                    <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-6 py-4 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] group-hover:bg-gray-50"><TableActionGroup>
                      <TableActionButton action="remove" label="Remove buddy" onClick={() => setDeleteConfirm(entry)} title="Remove" />
                    </TableActionGroup></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <FormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title="Add Buddy"
        size="md"
        formId="buddy-pool-form"
        isSubmitting={isSubmitting}
        submitLabel="Add Buddy"
      >
        <form id="buddy-pool-form" className="space-y-5" onSubmit={handleSubmit}>
          {formError && <FormErrorBanner message={formError} />}

          {/* Step 1 — area. Only states that actually have a deployed employee
              are listed, so a chosen area can never come back empty. */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700" htmlFor="bp-state">State</label>
              <select
                id="bp-state"
                value={stateKey}
                onChange={(e) => {
                  setStateKey(e.target.value);
                  setCityKey('');
                  setPicked(null);
                }}
                disabled={locationsLoading}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 disabled:bg-gray-50"
              >
                <option value="">{locationsLoading ? 'Loading…' : 'Select a state'}</option>
                {locations.map((l) => (
                  <option key={l.stateKey} value={l.stateKey}>
                    {l.state} ({l.employeeCount})
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700" htmlFor="bp-city">
                City <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <select
                id="bp-city"
                value={cityKey}
                onChange={(e) => {
                  setCityKey(e.target.value);
                  setPicked(null);
                }}
                disabled={!selectedState}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 disabled:bg-gray-50 disabled:text-gray-400"
              >
                <option value="">All cities in this state</option>
                {(selectedState?.cities ?? []).map((c) => (
                  <option key={c.cityKey} value={c.cityKey}>
                    {c.city} ({c.employeeCount})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p className="-mt-3 text-xs text-gray-500">
            Area comes from where each employee is currently deployed. Spelling variants are matched
            automatically, so "Jammu &amp; Kashmir" and "Jammu and Kashmir" are one area.
          </p>

          {/* Step 2 — the person, from whoever works in that area. */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Employee</label>
            {!stateKey ? (
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-6 text-center text-sm text-gray-500">
                Choose a state to see who works there.
              </div>
            ) : candidatesLoading ? (
              <div className="rounded-lg border border-gray-200 px-3 py-6 text-center text-sm text-gray-400">
                <InlineLoader label="Loading employees…" className="flex justify-center" />
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={candidateQuery}
                    onChange={(e) => setCandidateQuery(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    placeholder="Narrow by name, code or store..."
                    autoComplete="off"
                  />
                </div>
                {filteredCandidates.length === 0 ? (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-6 text-center text-sm text-gray-500">
                    {candidates.length === 0
                      ? 'No deployed employees in this area.'
                      : candidates.every((c) => c.inPool)
                        ? 'Everyone deployed in this area is already a buddy.'
                        : 'No employee here matches your search.'}
                  </div>
                ) : (
                  <ul className="max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200">
                    {filteredCandidates.map((c) => {
                      const isPicked = picked?.employeeCode === c.employeeCode;
                      return (
                        <li key={c.employeeCode}>
                          <button
                            type="button"
                            onClick={() => setPicked(c)}
                            className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors ${
                              isPicked ? 'bg-primary-50' : 'hover:bg-gray-50'
                            }`}
                          >
                            <span className="min-w-0">
                              <span className="flex flex-wrap items-center gap-1.5">
                                <span className="truncate text-sm font-medium text-gray-900">
                                  {c.employeeName || c.employeeCode}
                                </span>
                                <EmployeeTags
                                  designation={c.designation}
                                  onNotice={c.onNotice}
                                  lastWorkingDay={c.noticeLastWorkingDay}
                                />
                              </span>
                              <span className="block truncate text-xs text-gray-500">
                                {c.employeeCode}
                                {c.tenureLabel ? ` · ${c.tenureLabel}` : ''}
                                {c.storeName ? ` · ${c.storeName}` : ''}
                              </span>
                            </span>
                            <span className="flex shrink-0 items-center gap-2">
                              <span className="text-xs text-gray-400">{c.city}</span>
                              {isPicked && <CheckCircle2 className="h-5 w-5 text-primary-600" />}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            )}
          </div>
        </form>
      </FormModal>

      <ConfirmDeleteModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => deleteConfirm && handleDelete(deleteConfirm)}
        isSubmitting={isSubmitting}
        title="Remove Buddy"
        entityLabel="buddy"
        entityName={deleteConfirm?.employeeName ?? deleteConfirm?.employeeCode}
        consequenceNote="They stay on the project and keep their store — this only stops them being suggested for new joiners. Shadow trainings already assigned are unaffected."
        confirmLabel="Remove"
      />
    </div>
  );
}
